import { Box, Button, Link, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { questionnaireViewerService } from '../../features/questionnaireViewer/services/questionnaireViewerService';
import { RoutesUser } from './Routes';

const MenuHorizontal: React.FC = () => {
  const location = useLocation();
  const currentPath = location.pathname;

  const provasQuery = useQuery({
    queryKey: ['viewer-listProvas'],
    queryFn: ({ signal }) => questionnaireViewerService.listProvas(signal),
  });

  const hasProvas = Boolean(provasQuery.data && provasQuery.data.length > 0);

  const baseButtonStyles = {
    minWidth: { xs: '90px', sm: '100px', md: '120px', lg: '140px' },
    height: { xs: '32px', sm: '36px', md: '40px' },
    margin: '0 4px',
    borderRadius: '12px',
    textDecoration: 'none',
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    textTransform: 'none',

    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background:
        'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)',
      transform: 'translateX(-100%)',
      transition: 'transform 0.6s ease',
      zIndex: 1,
    },

    '&:hover::before': {
      transform: 'translateX(100%)',
    },
  };

  const inactiveButtonStyles = {
    ...baseButtonStyles,
    background: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    color: '#ffffff',

    '&:hover': {
      background: 'rgba(255, 255, 255, 0.2)',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      transform: 'translateY(-2px)',
      color: '#fff',
    },
  };

  const activeButtonStyles = {
    ...baseButtonStyles,
    background:
      'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
    backdropFilter: 'blur(12px)',
    color: '#667eea',

    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '3px',
      zIndex: 2,
    },

    '&:hover': {
      background:
        'linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(248, 250, 252, 1) 100%)',
      transform: 'translateY(-1px)',
    },
  };

  const disabledButtonStyles = {
    ...inactiveButtonStyles,
    opacity: 0.35,
    color: '#fff !important',
    filter: 'grayscale(30%)',
    cursor: 'not-allowed',
    pointerEvents: 'none',

    '&:hover': {},
  };

  return (
    <Box
      sx={{
        width: '80%',
        display: { xs: 'none', sm: 'flex', md: 'flex' },
        gap: { sm: '8px', md: '12px', lg: '16px' },
        justifyContent: 'center',
        alignItems: 'center',
        padding: '8px 16px',
        borderRadius: '16px',
        backdropFilter: 'blur(4px)',
      }}
    >
      {RoutesUser.map((route) => {
        const isActive = currentPath === route.path;
        const isDisabled = route.path === '/viewer/2' && !hasProvas;

        const buttonStyles = isDisabled
          ? disabledButtonStyles
          : isActive
            ? activeButtonStyles
            : inactiveButtonStyles;

        return (
          <Button
            key={route.path}
            component={isDisabled ? 'button' : Link}
            href={isDisabled ? undefined : route.path}
            disabled={isDisabled}
            sx={buttonStyles}
          >
            <Typography
              sx={{
                fontFamily: 'Roboto-Bold',
                fontSize: { xs: '9pt', sm: '9.5pt', md: '10pt', lg: '10.5pt' },
                letterSpacing: '0.3px',
                textTransform: 'uppercase',
                color: 'inherit',
                position: 'relative',
                zIndex: 2,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                textShadow: isActive
                  ? '0 1px 2px rgba(102, 126, 234, 0.1)'
                  : '0 1px 2px rgba(0,0,0,0.1)',
                userSelect: 'none',
              }}
            >
              {route.name}
            </Typography>
          </Button>
        );
      })}
    </Box>
  );
};

export default MenuHorizontal;
